import { transactionIdSetter } from "helpers/transactionIdFilter";
import { useTranslation } from "react-i18next";

import gridIcon from "assets/icons/grid-icon.svg";
import Filter from "components/global/Filter/Filter";
import { FilterItemTypes } from "configs/types";
import { useAppSelector } from "store";

export interface FilterOption {
  value: string | number;
  label: string;
}

export interface ConfiscationFilterValues {
  userIds: string;
  playerNickname: string;
  transactionCode: string;
  admins: FilterOption[];
  types: FilterOption[];
  currencies: FilterOption[];
  amountFrom: string;
  amountTo: string;
  createdFrom: string;
  createdTo: string;
}

interface Props extends ConfiscationFilterValues {
  disabled?: boolean;
  setUserIds: (value: string) => void;
  setPlayerNickname: (value: string) => void;
  setTransactionCode: (value: string) => void;
  setAdmins: (value: FilterOption[]) => void;
  setTypes: (value: FilterOption[]) => void;
  setCurrencies: (value: FilterOption[]) => void;
  setAmountFrom: (value: string) => void;
  setAmountTo: (value: string) => void;
  setCreatedRange: (from: string, to: string) => void;
  onApply: () => void;
  onReset: () => void;
}

const CONFISCATION_TYPES: FilterOption[] = [
  { value: "fund_confiscation", label: "Fund Confiscation" },
  { value: "other_debit", label: "Other Debit" },
  { value: "balance_transfer_debit", label: "Balance Transfer Debit" },
];

const mergeSelection = (
  selected: FilterOption[],
  values: FilterOption[],
  isDelete?: boolean,
) => {
  if (isDelete) return values;

  const existing = new Set(selected.map((item) => String(item.value)));
  const next = [...selected];

  values.forEach((item) => {
    if (!existing.has(String(item.value))) {
      next.push(item);
    }
  });

  return next;
};

const ConfiscationsFilters: React.FC<Props> = ({
  userIds,
  playerNickname,
  transactionCode,
  admins,
  types,
  currencies,
  amountFrom,
  amountTo,
  createdFrom,
  createdTo,
  disabled,
  setUserIds,
  setPlayerNickname,
  setTransactionCode,
  setAdmins,
  setTypes,
  setCurrencies,
  setAmountFrom,
  setAmountTo,
  setCreatedRange,
  onApply,
  onReset,
}) => {
  const { t } = useTranslation();
  const currencyCodes = useAppSelector((store) => store.settings.currencies);
  const currencyOptions = currencyCodes.map((currency) => ({
    value: currency,
    label: currency,
  }));

  return (
    <Filter
      disabled={disabled}
      handleApply={onApply}
      handleClear={onReset}
      filterItems={[
        {
          type: FilterItemTypes.input,
          title: "Transaction Code",
          boxTitle: "Transaction Code",
          icon: gridIcon,
          inputValue: transactionCode,
          setInputValue: setTransactionCode,
        },
        {
          type: FilterItemTypes.input,
          title: "Player ID",
          boxTitle: "Player ID",
          icon: gridIcon,
          inputValue: userIds,
          setInputValue: transactionIdSetter(setUserIds, true),
        },
        {
          type: FilterItemTypes.input,
          title: "Player Nickname",
          boxTitle: "Player Nickname",
          icon: gridIcon,
          inputValue: playerNickname,
          setInputValue: setPlayerNickname,
        },
        {
          type: FilterItemTypes.input,
          title: "Amount",
          boxTitle: "Amount From",
          secondBoxTitle: "Amount To",
          icon: gridIcon,
          inputValue: amountFrom,
          setInputValue: setAmountFrom,
          secondInputValue: amountTo,
          setSecondInputValue: setAmountTo,
        },
        {
          type: FilterItemTypes.multiselect,
          title: "Type",
          boxTitle: "Confiscation Type",
          icon: gridIcon,
          itemsArray: CONFISCATION_TYPES,
          activeItems: types,
          handleApply: (values, isDelete) =>
            setTypes(mergeSelection(types, values, isDelete)),
        },
        {
          type: FilterItemTypes.chipInput,
          title: "Admin",
          boxTitle: "Admin",
          icon: gridIcon,
          activeItems: admins,
          handleApply: (values, isDelete) =>
            setAdmins(mergeSelection(admins, values, isDelete)),
        },
        {
          type: FilterItemTypes.multiselect,
          title: t("currencies"),
          boxTitle: t("filterCurrencies"),
          icon: gridIcon,
          itemsArray: currencyOptions,
          activeItems: currencies,
          handleApply: (values, isDelete) =>
            setCurrencies(mergeSelection(currencies, values, isDelete)),
          disabled: currencyOptions.length === 0,
        },
        {
          type: FilterItemTypes.datePicker,
          title: "Created At",
          boxTitle: "Created At",
          icon: gridIcon,
          firstDatePlaceholder: "Created From",
          secondDatePlaceholder: "Created To",
          firstDateInitValue: createdFrom,
          secondDateInitValue: createdTo,
          handleApplyDate: (from, to) => setCreatedRange(from, to ?? ""),
          activeItems: [
            ...(createdFrom
              ? [{ value: "from", label: `From: ${createdFrom}` }]
              : []),
            ...(createdTo ? [{ value: "to", label: `To: ${createdTo}` }] : []),
          ],
          onDeleteItem: (value) =>
            setCreatedRange(
              value === "from" ? "" : createdFrom,
              value === "to" ? "" : createdTo,
            ),
        },
      ]}
    />
  );
};

export default ConfiscationsFilters;
